import crqlarMark from "../../../assets/media/images/crqlar-logo.png";

export const openBlobInTab = (blob: Blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
};

export const downloadBlobAsFile = (blob: Blob, fileName: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

const LOGO_RASTER_SIZE = 600;

const blobToDataUrl = (blob: Blob) => new Promise<string | undefined>(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : undefined);
    reader.onerror = () => resolve(undefined);
    reader.readAsDataURL(blob);
});

const rasterizeToPng = (source: string) => new Promise<string | undefined>(resolve => {
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
        const width = image.naturalWidth || image.width || LOGO_RASTER_SIZE;
        const height = image.naturalHeight || image.height || Math.round(LOGO_RASTER_SIZE / 3);
        const scale = Math.min(LOGO_RASTER_SIZE / width, LOGO_RASTER_SIZE / height, 1);

        try {
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(width * scale));
            canvas.height = Math.max(1, Math.round(height * scale));

            const context = canvas.getContext('2d');
            if (!context) return resolve(undefined);

            context.drawImage(image, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL('image/png'));
        } catch {
            resolve(undefined);
        }
    };

    image.onerror = () => resolve(undefined);
    image.src = source;
});

export const fetchLogoDataUrl = async (url: string | undefined): Promise<string | undefined> => {
    if (!url) return undefined;

    try {
        const response = await fetch(url, {mode: 'cors'});
        if (!response.ok) return undefined;

        const blob = await response.blob();
        if (!blob.type.startsWith('image/')) return undefined;

        const dataUrl = await blobToDataUrl(blob);
        if (!dataUrl) return undefined;

        return blob.type.includes('svg') ? rasterizeToPng(dataUrl) : dataUrl;
    } catch {
        return undefined;
    }
};

export const slugifyForFileName = (value: string | undefined, fallback: string) => {
    const slug = (value ?? '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);

    return slug || fallback;
};

export const exportDaySlug = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
].join('-');

export const CRQLAR_BRANDING_URL = 'https://crqlar.com';

export type SurveyExportBranding = {
    label: string
    url: string
    markDataUrl?: string
};

export const loadSurveyExportBranding = async (
    t: (key: string) => string
): Promise<SurveyExportBranding> => ({
    label: t('Powered by Crqlar.com'),
    url: CRQLAR_BRANDING_URL,
    markDataUrl: await fetchLogoDataUrl(crqlarMark),
});

export const dataUrlToUint8Array = (dataUrl: string | undefined): Uint8Array | undefined => {
    if (!dataUrl) return undefined;

    const base64 = dataUrl.split(',')[1];
    if (!base64) return undefined;

    try {
        const binary = window.atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index);
        return bytes;
    } catch {
        return undefined;
    }
};
